import { useState, useRef, useEffect } from "react";
import { parseISO } from "date-fns";
import {
    HOUR_HEIGHT_PX,
    MIN_DURATION_MINUTES,
    LONG_PRESS_DURATION,
    getAppointmentPosition,
    getTimeFromOffset,
    formatForInput
} from "@/utils/timelineUtils";

/**
 * @typedef {import('@/types/schema').Appointment} Appointment
 */

/**
 * Custom hook to handle complex timeline interactions:
 * - Drag and Drop (Mouse)
 * - Long Press Drag (Touch)
 * - Resize (Mouse)
 * - Slot Click
 */
export function useTimelineInteractions({
    onAppointmentReschedule,
    onAppointmentResize,
    onSlotClick
}) {
    // Drag and drop state
    const [draggingAppointment, setDraggingAppointment] = useState(null);
    const [dragOverDate, setDragOverDate] = useState(null);
    const [dragOverY, setDragOverY] = useState(null);

    // Touch long-press state
    const [touchTimer, setTouchTimer] = useState(null);
    const [isDragEnabled, setIsDragEnabled] = useState(false);

    // Refs
    const dragGrabOffsetRef = useRef(0);
    const touchStartPosRef = useRef({ x: 0, y: 0 });
    const resizeDataRef = useRef({ appointment: null, startY: 0, originalHeight: 0 });

    // Resize state
    const [isResizing, setIsResizing] = useState(false);
    const [resizingAptId, setResizingAptId] = useState(null);
    const [resizeCurrentHeight, setResizeCurrentHeight] = useState(0);

    // ==========================================
    // Slot Click
    // ==========================================
    const handleSlotClick = (e, date) => {
        // Don't trigger if clicking on an appointment
        if (e.target.closest('[data-appointment]')) return;
        if (!onSlotClick) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const offsetY = e.clientY - rect.top;
        const clickedTime = getTimeFromOffset(offsetY, date);

        onSlotClick(formatForInput(clickedTime));
    };

    // ==========================================
    // Mouse Drag & Drop
    // ==========================================
    const handleDragStart = (e, apt) => {
        if (apt.is_anonymized || apt.status !== 'scheduled') {
            e.preventDefault();
            return;
        }

        // Calculate offset from top of card where user grabbed
        const cardRect = e.currentTarget.getBoundingClientRect();
        dragGrabOffsetRef.current = e.clientY - cardRect.top;

        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', apt.id);
        setDraggingAppointment(apt);
    };

    const handleDragOver = (e, date) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const rect = e.currentTarget.getBoundingClientRect();
        const offsetY = e.clientY - rect.top - dragGrabOffsetRef.current;

        setDragOverDate(date);
        setDragOverY(Math.max(0, offsetY));
    };

    const handleDragLeave = () => {
        setDragOverDate(null);
        setDragOverY(null);
    };

    const handleDrop = (e, date) => {
        e.preventDefault();

        if (!draggingAppointment || !onAppointmentReschedule) {
            resetDragState();
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const offsetY = e.clientY - rect.top - dragGrabOffsetRef.current;
        const newTime = getTimeFromOffset(Math.max(0, offsetY), date);

        onAppointmentReschedule(draggingAppointment.id, formatForInput(newTime));
        resetDragState();
    };

    const handleDragEnd = () => {
        resetDragState();
    };

    const resetDragState = () => {
        setDraggingAppointment(null);
        setDragOverDate(null);
        setDragOverY(null);
        dragGrabOffsetRef.current = 0;
    };

    // ==========================================
    // Touch Drag (Long Press)
    // ==========================================
    const handleTouchStart = (e, apt) => {
        if (apt.is_anonymized || apt.status !== 'scheduled') return;

        const touch = e.touches[0];
        touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

        const cardRect = e.currentTarget.getBoundingClientRect();
        dragGrabOffsetRef.current = touch.clientY - cardRect.top;

        const timer = setTimeout(() => {
            setIsDragEnabled(true);
            setDraggingAppointment(apt);
            if (navigator.vibrate) navigator.vibrate(50);
        }, LONG_PRESS_DURATION);

        setTouchTimer(timer);
    };

    const handleTouchMove = (e) => {
        // Cancel timer if scrolling occurs
        if (touchTimer && !isDragEnabled) {
            const touch = e.touches[0];
            const dx = touch.clientX - touchStartPosRef.current.x;
            const dy = touch.clientY - touchStartPosRef.current.y;

            if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
                clearTimeout(touchTimer);
                setTouchTimer(null);
            }
            return;
        }

        if (!isDragEnabled || !draggingAppointment) return;

        if (e.cancelable) e.preventDefault();

        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        const dayColumn = element?.closest('[data-day-column]');

        if (dayColumn) {
            const dateStr = dayColumn.getAttribute('data-day-column');
            const rect = dayColumn.getBoundingClientRect();
            const offsetY = touch.clientY - rect.top - dragGrabOffsetRef.current;

            setDragOverDate(parseISO(dateStr));
            setDragOverY(Math.max(0, offsetY));
        }
    };

    const handleTouchEnd = () => {
        if (touchTimer) {
            clearTimeout(touchTimer);
            setTouchTimer(null);
        }

        if (isDragEnabled && draggingAppointment && dragOverDate && onAppointmentReschedule) {
            const newTime = getTimeFromOffset(dragOverY, dragOverDate);
            onAppointmentReschedule(draggingAppointment.id, formatForInput(newTime));
        }

        setIsDragEnabled(false);
        resetDragState();
    };

    // ==========================================
    // Resize (Mouse only)
    // ==========================================
    const handleResizeStart = (e, apt) => {
        e.stopPropagation();
        e.preventDefault();
        if (apt.is_anonymized || apt.status !== 'scheduled') return;

        const { height } = getAppointmentPosition(apt.date_time, apt.duration_minutes);
        resizeDataRef.current = {
            appointment: apt,
            startY: e.clientY,
            originalHeight: height
        };
        setIsResizing(true);
        setResizingAptId(apt.id);
        setResizeCurrentHeight(height);

        // Bind global listeners
        document.addEventListener('mousemove', onResizeMove);
        document.addEventListener('mouseup', onResizeEnd);
    };

    const onResizeMove = (moveEvent) => {
        const deltaY = moveEvent.clientY - resizeDataRef.current.startY;
        const newHeight = Math.max(
            resizeDataRef.current.originalHeight + deltaY,
            MIN_DURATION_MINUTES / 60 * HOUR_HEIGHT_PX
        );
        resizeDataRef.current.currentHeight = newHeight;
        setResizeCurrentHeight(newHeight);
    };

    const onResizeEnd = () => {
        document.removeEventListener('mousemove', onResizeMove);
        document.removeEventListener('mouseup', onResizeEnd);

        if (resizeDataRef.current.appointment && onAppointmentResize) {
            const finalHeight = resizeDataRef.current.currentHeight || resizeDataRef.current.originalHeight;
            const newDuration = Math.max(
                Math.round((finalHeight / HOUR_HEIGHT_PX) * 60 / 15) * 15,
                MIN_DURATION_MINUTES
            );

            if (newDuration !== resizeDataRef.current.appointment.duration_minutes) {
                onAppointmentResize(resizeDataRef.current.appointment.id, newDuration);
            }
        }

        resizeDataRef.current = { appointment: null, startY: 0, originalHeight: 0 };
        setIsResizing(false);
        setResizingAptId(null);
        setResizeCurrentHeight(0);
    };

    // Cleanup listeners on unmount
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', onResizeMove);
            document.removeEventListener('mouseup', onResizeEnd);
        };
    }, []);

    return {
        // State
        draggingAppointment,
        dragOverDate,
        dragOverY,
        isDragEnabled,
        isResizing,
        resizingAptId,
        resizeCurrentHeight,

        // Handlers
        handleSlotClick,
        handleDragStart,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragEnd,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleResizeStart
    };
}
